import { isEqual, unionWith } from 'lodash-es';
import { useMemo } from 'react';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { preferenceCache } from 'services/cache';
import { localPhoneParse, localPhoneStringify, LocalPhone } from 'utils/phoneHelpers';

export enum LoginMode {
  Password,
  SMS,
}
export interface LoginPreferenceRaw {
  mode: LoginMode;
  phones: string[];
}
export interface LoginPreference {
  mode: LoginMode;
  phones: LocalPhone[];
}
const defaultPreference: LoginPreference = {
  mode: LoginMode.Password,
  phones: [],
};

let preference = defaultPreference;
const getLoginPreference = (refresh = false) => {
  if (refresh) {
    const raw = preferenceCache.getItem('login') as LoginPreferenceRaw;
    const phones = raw?.phones
      ? raw.phones.reduce((res, it) => {
          const target = localPhoneParse(it);
          if (target) {
            res.push(target);
          }
          return res;
        }, [] as LoginPreference['phones'])
      : defaultPreference.phones;
    preference = { mode: raw?.mode || LoginMode.Password, phones };
  }
  return preference;
};
const updateLoginPreference = (updater: Partial<LoginPreference>) => {
  const { mode = preference.mode, phones = preference.phones } = updater;
  const nextPhones = unionWith(phones, preference.phones, isEqual);
  preference = { mode, phones: nextPhones };
  const nextPhonesRaw = phones.map(localPhoneStringify);
  preferenceCache.setItem<LoginPreferenceRaw>('login', { mode, phones: nextPhonesRaw });
  // update返回的对象会直接使用在recoil state setter里，
  // recoil setter会将这个引用本身转换为#Object，从而会导致所有的key无法操作，
  // 因此需要进行一次shallow copy.
  return { ...preference };
};
const removePhones = (target: LoginPreference['phones']) => {
  const current = preference.phones;
  const next = current.reduce((rst, it) => {
    if (target.some((itt) => itt.code !== it.code || itt.phone !== it.phone)) {
      rst.push(it);
    }
    return rst;
  }, [] as LocalPhone[]);
  preference.phones = next;
  return updateLoginPreference(preference);
};
export const loginPreferenceState = atom(getLoginPreference(true));

const useLoginPreference = () => {
  const data = useAtomValue(loginPreferenceState);
  const setLoginPreference = useSetAtom(loginPreferenceState);
  const phoneTexts = useMemo(() => data.phones.map(localPhoneStringify), [data.phones]);
  return {
    ...data,
    phoneTexts,
    changeMode: (mode: LoginPreference['mode']) =>
      setLoginPreference(updateLoginPreference({ mode })),
    addPhones: (phones: LocalPhone | LocalPhone[]) =>
      setLoginPreference(
        updateLoginPreference({ phones: Array.isArray(phones) ? phones : [phones] }),
      ),
    removePhones: (target: LoginPreference['phones']) => setLoginPreference(removePhones(target)),
  };
};

export default useLoginPreference;
